import { ReactNode, RefObject, SyntheticEvent, useEffect, useRef } from 'react';
import { SxProps, Tab, Tabs, TabsProps, Theme } from '@mui/material';
import { TabList, useTabContext } from '@mui/lab';

interface QcsTabItem<TId extends string = string> {
  id: TId;
  label: string;
}

type BaseProps = Omit<TabsProps, 'value' | 'onChange' | 'children'>;

interface ItemsProps<TId extends string = string> extends BaseProps {
  tabs: QcsTabItem<TId>[];
  value: TId;
  onChange: (event: SyntheticEvent, id: TId) => void;
  children?: never;
}

interface ChildrenProps extends BaseProps {
  tabs?: never;
  children: ReactNode;
  //Inside a <TabContext> the selected tab comes from the context, so only onChange is needed.
  value?: TabsProps['value'];
  onChange?: TabsProps['onChange'];
}

type Props<TId extends string = string> = ItemsProps<TId> | ChildrenProps;

const isItemsMode = <TId extends string>(props: Props<TId>): props is ItemsProps<TId> => props.tabs !== undefined;

/** Compact tab bar with smaller height, no uppercase, and a rounded indicator. */
const COMPACT_SX: SxProps<Theme> = {
  minHeight: 36,
  '& .MuiTab-root': {
    minHeight: 36,
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    padding: '6px 16px',
    letterSpacing: '0.01em',
  },
};

//Turns a vertical wheel over the tab bar into horizontal scrolling, so overflowing tabs can be
//reached with the mouse alone, without aiming for the scroll buttons.
const useWheelScroll = (): RefObject<HTMLDivElement | null> => {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = rootRef.current;
    if (!element) return;
    const scroller = element.querySelector('.MuiTabs-scroller') as HTMLElement | null;
    if (!scroller) return;
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      //Nothing to scroll: leave the event alone so the page keeps scrolling as usual.
      if (maxScroll <= 0) return;
      e.preventDefault();
      scroller.scrollBy({ left: e.deltaY * 2.5, behavior: 'smooth' });
    };
    scroller.addEventListener('wheel', handleWheel, { passive: false });
    return () => scroller.removeEventListener('wheel', handleWheel);
  }, []);

  return rootRef;
};

/**
 * Tab bar with horizontal scrolling built in — scroll buttons plus mouse wheel over the tabs.
 * Renders a `TabList` inside a `<TabContext>` and plain `Tabs` outside of it.
 *
 * Pass `tabs` for a compact, id-driven bar, or `<Tab>` children for the default look.
 */
export const QcsTabs = <TId extends string = string>(props: Props<TId>) => {
  const rootRef = useWheelScroll();
  const inTabContext = useTabContext() !== null;

  let content: ReactNode;
  let value: TabsProps['value'];
  let handleChange: TabsProps['onChange'];
  let sx: SxProps<Theme> | undefined;
  let rest: BaseProps;

  if (isItemsMode(props)) {
    const { tabs, value: selectedId, onChange, sx: sxProp, ...others } = props;
    content = tabs.map((tab) => <Tab key={tab.id} label={tab.label} />);
    value = tabs.findIndex((tab) => tab.id === selectedId);
    handleChange = (event, index) => onChange(event, tabs[index].id);
    sx = [COMPACT_SX, ...(Array.isArray(sxProp) ? sxProp : [sxProp])];
    rest = others;
  } else {
    const { children, value: valueProp, onChange, sx: sxProp, ...others } = props;
    content = children;
    value = valueProp;
    handleChange = onChange;
    sx = sxProp;
    rest = others;
  }

  const tabsProps = {
    variant: 'scrollable',
    scrollButtons: 'auto',
    allowScrollButtonsMobile: true,
    ...rest,
    ref: rootRef,
    onChange: handleChange,
    sx,
  } satisfies TabsProps;

  //TabList takes its value from the surrounding TabContext and rejects the prop.
  return inTabContext ? (
    <TabList {...tabsProps}>{content}</TabList>
  ) : (
    <Tabs {...tabsProps} value={value}>
      {content}
    </Tabs>
  );
};
